/**
 * useStreamPass — hold a short-lived playback credential and keep it alive.
 *
 * Ticket 86d40p593. Extracted verbatim from the community-recording route,
 * which was the only surface that had ever done this. It is being lifted out
 * because four more surfaces need exactly the same behaviour — lessons, events,
 * chat and masterminds — and writing it five times guarantees five subtly
 * different answers to "what happens when a pass expires mid-watch".
 *
 * WHAT LIVES HERE AND WHY. Everything that is a policy rather than a place:
 * when to renew, what to do when renewal fails, which refusals are final, and
 * how to avoid stampeding the server. Each surface supplies only the endpoint
 * it asks, because that is the only thing that genuinely differs between them.
 *
 * THE PROBLEM IT SOLVES. A Cloudflare Stream pass is not a turnstile — it is
 * stamped into every segment URL and re-checked on each chunk, so a playing
 * video STOPS when the pass expires. Measured 13 Sep on a protected video: the
 * `p` and `s` segment parameters carry our own `exp`, `nbf`, `sub` and `kid`
 * and are minted afresh on each playlist fetch. Reasoning from an UNPROTECTED
 * video gives the opposite answer, because there the parameters are static.
 * Renewal is therefore mandatory, not a nicety.
 *
 * WHAT IT DELIBERATELY DOES NOT DO. It does not touch the player. Restoring the
 * viewer's position after a swap belongs to whoever owns the player instance,
 * because only they know what a "position" means for their surface. The hook
 * calls `onBeforeSwap` at the moment to capture it and leaves the rest alone.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { EDGE_FUNCTION_HEADERS } from '@/lib/edge-functions';

/**
 * Renew at 80% of the pass's real lifetime, measured from the token's own iat
 * and exp. Never from the device clock — a laptop a few minutes out would
 * otherwise renew constantly or never renew at all.
 */
export const RENEW_AT_FRACTION = 0.8;

/**
 * Cap only the renewals driven by a playback error. Without it a member whose
 * access was revoked — or a player failing for a reason a fresh pass cannot fix
 * — retries forever against a door that will never open. This count never
 * resets, so three is three per page visit.
 *
 * Renewals driven by the timer are NOT capped. A long viewing legitimately
 * crosses several pass boundaries; capping those would cut someone off part-way
 * through a recording they are entitled to watch.
 */
export const MAX_ERROR_RENEWALS = 3;

/**
 * Backoff for a renewal that failed. Doubling from 5s to a 60s ceiling, with
 * ±25% jitter so a Supabase blip does not bring every viewer back in the same
 * second. Attempt 0 is immediate — a pass may have genuinely expired and the
 * first thing to try is simply asking again.
 */
export const RETRY_BASE_MS = 5_000;
export const RETRY_MAX_MS = 60_000;

export function retryDelayMs(attempt: number): number {
  if (attempt <= 0) return 0;
  const flat = Math.min(RETRY_BASE_MS * 2 ** (attempt - 1), RETRY_MAX_MS);
  return Math.round(flat * (0.75 + Math.random() * 0.5));
}

/**
 * When a pass minted at `iat` and expiring at `exp` should be renewed, as a
 * delay in milliseconds from now.
 *
 * Derived from the token's OWN claims rather than the device clock, and floored
 * at five seconds so a pathologically short pass cannot spin the renewal loop.
 * Exported so the policy can be asserted without rendering a player.
 */
export function renewalDelayMs(iat: number, exp: number): number | null {
  if (!Number.isFinite(iat) || !Number.isFinite(exp) || exp <= iat) return null;
  return Math.max((exp - iat) * 1000 * RENEW_AT_FRACTION, 5000);
}

export interface StreamSource {
  src: string;
  type: 'application/x-mpegurl' | 'video/mp4';
}

/** The two shapes a pass endpoint may answer with. */
interface PassResponse {
  mode?: 'stream' | 'proxy';
  url?: string;
  token?: string;
  iat?: number;
  exp?: number;
}

/**
 * The issuer answered "this row is not on Stream" (409). Not an error and not a
 * refusal — the caller keeps whatever address it already had. Distinct from a
 * failure so a player falls back silently instead of showing a member an error
 * for a video that is playing perfectly well from R2.
 */
export const STREAM_UNAVAILABLE = 'stream-unavailable';

/** Thrown when the server refused; `fatal` means stop, do not retry. */
export class PassError extends Error {
  constructor(
    message: string,
    readonly fatal: boolean,
  ) {
    super(message);
  }
}

export interface UseStreamPassOptions {
  /**
   * Stable identity of the thing being watched. Changing it restarts with a
   * fresh retry budget — navigating between two recordings in one session must
   * not leave the second with the first one's used retries.
   */
  resourceId: string;
  /** Builds the token request URL from the Supabase functions base. */
  tokenUrl: (base: string) => string;
  /**
   * Builds the byte-proxy URL when the server answers `mode: 'proxy'`. A
   * surface that has no proxy path may omit it; a proxy answer then fails
   * rather than silently producing a broken source.
   */
  proxyUrl?: (base: string, token: string) => string;
  /**
   * Called immediately before a fresh source is swapped in, so the caller can
   * capture playback position and playing state. Changing `src` makes every
   * player reload from the start, paused.
   */
  onBeforeSwap?: () => void;
  /** Skip entirely — e.g. a row that is not on Stream and plays from R2. */
  enabled?: boolean;
}

export interface UseStreamPassResult {
  source: StreamSource | null;
  error: string | null;
  /** The issuer said this row is not on Stream. Fall back, do not show an error. */
  unavailable: boolean;
  /** Wire to the player's onError. Asks for a fresh pass rather than guessing. */
  renewOnError: () => void;
  /** True once a pass has been fetched at least once. */
  ready: boolean;
}

export function useStreamPass(opts: UseStreamPassOptions): UseStreamPassResult {
  const { resourceId, tokenUrl, proxyUrl, onBeforeSwap, enabled = true } = opts;

  const [source, setSource] = useState<StreamSource | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [unavailable, setUnavailable] = useState(false);

  const errorRenewalsRef = useRef(0);
  const renewTimerRef = useRef<number | null>(null);
  const renewAtRef = useRef<number | null>(null);
  const passExpiresAtRef = useRef<number | null>(null);
  const scheduledFailuresRef = useRef(0);
  const retryPendingRef = useRef(false);
  const renewingRef = useRef(false);
  const cancelledRef = useRef(false);

  // Held in refs so the effects below do not re-run when a caller passes an
  // inline arrow function, which is the normal way to call this.
  const tokenUrlRef = useRef(tokenUrl);
  const proxyUrlRef = useRef(proxyUrl);
  const onBeforeSwapRef = useRef(onBeforeSwap);
  tokenUrlRef.current = tokenUrl;
  proxyUrlRef.current = proxyUrl;
  onBeforeSwapRef.current = onBeforeSwap;

  /**
   * Ask the edge function for a pass. It re-runs the membership check every
   * time, which is what makes renewal safe: a member who lost access gets a
   * refusal here rather than a fresh pass.
   */
  const fetchPass = useCallback(async (): Promise<{
    source: StreamSource;
    iat?: number;
    exp?: number;
  }> => {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session?.access_token) throw new PassError('Not signed in', true);

    const base = import.meta.env.VITE_SUPABASE_URL;
    const res = await fetch(tokenUrlRef.current(base), {
      method: 'GET',
      headers: { ...EDGE_FUNCTION_HEADERS, Authorization: `Bearer ${session.access_token}` },
    });

    // The server answers 404 both for "no such thing" and "not yours" — it will
    // not say which, on purpose, and the chat surface's share preview already
    // conflates them the same way. Either answer means asking again cannot
    // change anything, so this stops rather than burning the renewal budget on
    // a door that will not open. 403 is the token/resource mismatch; also final.
    if (res.status === 404 || res.status === 403) {
      throw new PassError('This recording is not available to you.', true);
    }
    if (res.status === 401) {
      // Our own sign-in expired rather than the pass. Refresh once and let the
      // caller retry; a second 401 falls through as fatal below.
      const { data } = await supabase.auth.refreshSession();
      if (!data.session) throw new PassError('Your session expired. Please sign in again.', true);
      throw new PassError('retry-after-refresh', false);
    }
    if (res.status === 409) {
      // Not on Stream, or on Stream but not verified, or this environment
      // cannot sign. All three mean the same thing to a player: use the address
      // you already have. Fatal so it stops asking, not so it shows an error.
      throw new PassError(STREAM_UNAVAILABLE, true);
    }
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new PassError(body.error || `Token request failed (${res.status})`, false);
    }

    const pass = (await res.json()) as PassResponse;

    if (pass.mode === 'stream') {
      if (!pass.url) throw new PassError('Stream response missing url', false);
      return {
        source: { src: pass.url, type: 'application/x-mpegurl' },
        iat: pass.iat,
        exp: pass.exp,
      };
    }

    // Proxy path — unchanged from before Stream existed.
    if (!pass.token) throw new PassError('Token response missing token', false);
    const build = proxyUrlRef.current;
    if (!build) {
      throw new PassError(
        'Server answered with a proxy token but this surface has no proxy path',
        false,
      );
    }
    return {
      source: { src: build(base, pass.token), type: 'video/mp4' },
      iat: pass.iat,
      exp: pass.exp,
    };
  }, []);

  /** Schedule the quiet renewal from the pass's own claims. */
  const scheduleRenewal = useCallback((iat?: number, exp?: number, onDue?: () => void) => {
    if (renewTimerRef.current !== null) window.clearTimeout(renewTimerRef.current);
    renewTimerRef.current = null;
    renewAtRef.current = null;
    passExpiresAtRef.current = null;
    if (typeof iat !== 'number' || typeof exp !== 'number' || exp <= iat) return;
    passExpiresAtRef.current = exp * 1000;

    const dueInMs = renewalDelayMs(iat, exp);
    if (dueInMs === null) return;
    renewAtRef.current = Date.now() + dueInMs;
    renewTimerRef.current = window.setTimeout(() => onDue?.(), dueInMs);
  }, []);

  /** Swap in a fresh pass without losing the viewer's place. */
  const renew = useCallback(
    async (reason: 'scheduled' | 'error') => {
      if (renewingRef.current || cancelledRef.current) return;
      // A retry is already armed. Without this a player that errors repeatedly
      // burns the whole error budget in a second and never waits out the backoff.
      if (retryPendingRef.current && reason === 'error') return;
      retryPendingRef.current = false;
      if (reason === 'error' && errorRenewalsRef.current >= MAX_ERROR_RENEWALS) {
        setError('Playback kept failing. Reload the page to try again.');
        return;
      }
      renewingRef.current = true;
      if (reason === 'error') errorRenewalsRef.current += 1;
      try {
        onBeforeSwapRef.current?.();
        let next;
        try {
          next = await fetchPass();
        } catch (err) {
          // One retry for the refresh-the-session case, which is not a failure.
          if (err instanceof PassError && !err.fatal && err.message === 'retry-after-refresh') {
            next = await fetchPass();
          } else {
            throw err;
          }
        }
        if (cancelledRef.current) return;
        scheduledFailuresRef.current = 0;
        setSource(next.source);
        scheduleRenewal(next.iat, next.exp, () => void renew('scheduled'));
      } catch (err) {
        if (cancelledRef.current) return;

        // Refused outright — no amount of waiting changes the answer. Stop, and
        // disarm the timer too, or the next scheduled renewal fires against a
        // door we already know is shut.
        if (err instanceof PassError && err.fatal) {
          setError(err.message);
          errorRenewalsRef.current = MAX_ERROR_RENEWALS;
          if (renewTimerRef.current !== null) window.clearTimeout(renewTimerRef.current);
          renewTimerRef.current = null;
          renewAtRef.current = null;
          passExpiresAtRef.current = null;
          return;
        }

        const armRetry = (delayMs: number, nextReason: 'scheduled' | 'error') => {
          retryPendingRef.current = true;
          if (renewTimerRef.current !== null) window.clearTimeout(renewTimerRef.current);
          renewTimerRef.current = window.setTimeout(() => void renew(nextReason), delayMs);
        };

        if (reason === 'scheduled') {
          // We renew at 80% of the pass's life, so the one in hand is still good
          // for the remaining fifth. A blip at that moment is not a reason to
          // stop a video that is playing fine, so back off and try again without
          // showing anything. Retries stop at the point the pass actually
          // expires; from there the player's own failure runs the capped path.
          scheduledFailuresRef.current += 1;
          const expiresAt = passExpiresAtRef.current;
          const roomMs = expiresAt === null ? 0 : expiresAt - Date.now() - 5000;
          const delayMs = retryDelayMs(scheduledFailuresRef.current);
          if (roomMs > 0) {
            armRetry(Math.min(delayMs, roomMs), 'scheduled');
            return;
          }
          return;
        }

        // Error-driven. Budget left, so wait out the backoff and ask again
        // ourselves rather than sitting idle until the player happens to fail a
        // second time.
        if (errorRenewalsRef.current < MAX_ERROR_RENEWALS) {
          armRetry(retryDelayMs(errorRenewalsRef.current), 'error');
          return;
        }
        setError(err instanceof Error ? err.message : 'Playback failed');
      } finally {
        renewingRef.current = false;
      }
    },
    [fetchPass, scheduleRenewal],
  );

  // Initial load.
  useEffect(() => {
    if (!enabled) return;
    cancelledRef.current = false;
    // Fresh budget per resource.
    errorRenewalsRef.current = 0;
    scheduledFailuresRef.current = 0;
    retryPendingRef.current = false;
    setError(null);
    setUnavailable(false);

    const load = async () => {
      try {
        let first;
        try {
          first = await fetchPass();
        } catch (err) {
          if (err instanceof PassError && err.message === 'retry-after-refresh') {
            first = await fetchPass();
          } else {
            throw err;
          }
        }
        if (cancelledRef.current) return;
        setSource(first.source);
        scheduleRenewal(first.iat, first.exp, () => void renew('scheduled'));
      } catch (err) {
        if (cancelledRef.current) return;
        if (err instanceof PassError && err.message === STREAM_UNAVAILABLE) {
          setUnavailable(true);
          return;
        }
        setError(err instanceof Error ? err.message : 'Failed to load recording');
      }
    };
    void load();
    return () => {
      cancelledRef.current = true;
      if (renewTimerRef.current !== null) window.clearTimeout(renewTimerRef.current);
    };
  }, [resourceId, enabled, fetchPass, scheduleRenewal, renew]);

  /**
   * Browsers freeze background timers to save battery, so a tab left open or a
   * sleeping laptop can sail past the renewal point without it firing. Re-check
   * whenever the tab becomes visible again.
   */
  useEffect(() => {
    if (!enabled) return;
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      const due = renewAtRef.current;
      if (due !== null && Date.now() >= due) void renew('scheduled');
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [renew, enabled]);

  const renewOnError = useCallback(() => {
    if (error) return;
    void renew('error');
  }, [error, renew]);

  return { source, error, unavailable, renewOnError, ready: source !== null };
}
